import React, { useState } from 'react';
import { colors, Icons } from '@united-talent-agency/components';

import DeleteModal from './Modal';
import { DeleteContainer } from './styles';
import { CALL_LIST } from '../../support/cypressTags';
import {
  useBulkDeleteCallRecordMutation,
  useCallRecordsQuery,
  CallRecordsDocument,
  CallRecordsDuplicateCheckDocument,
} from '../../generated/graphql';

const BulkDeleteCommunication = ({
  user,
  desk,
  selectAll,
  filterParams,
  selectExcept = new Set(),
  callsToDo = [],
  onExit,
  onUpdateApplied,
  hideFutureCalls = false,
}) => {
  const [bulkDeleteCallRecords] = useBulkDeleteCallRecordMutation({
    refetchQueries: [CallRecordsDocument, CallRecordsDuplicateCheckDocument],
  });
  const { refetch } = useCallRecordsQuery({
    skip: true,
    fetchPolicy: 'standby',
  });

  // States
  const [bulkDeleteImpacted, setBulkDeleteImpacted] = useState(0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const { DeleteIcon } = Icons;
  const someSelected = selectAll || selectExcept.size > 0;

  /** Submits call_todo data to the API for bulk delete.
   *
   * @returns {void}
   **/
  const submitBulkDelete = async () => {
    setShowDeleteModal(false);

    if (!bulkDeleteImpacted) {
      return;
    }

    // Calculate affected records for internal count updates
    const affectedRecords = selectAll
      ? callsToDo.filter((call) => !selectExcept.has(call._id))
      : callsToDo.filter((call) => selectExcept.has(call._id));

    const selection = Array.from(selectExcept);

    // Check if filters are applied (search term, status filters, or hide future calls)
    const hasFilters =
      filterParams?.query ||
      (filterParams?.filter && filterParams.filter.length > 0) ||
      hideFutureCalls;

    try {
      if (selectAll && hasFilters) {
        // When filters are applied, we must use explicit IDs to respect the filtered results
        // Using 'except' would operate on ALL desk records, not just filtered ones
        const explicitIds = callsToDo
          .filter((call) => !selectExcept.has(call._id))
          .map((call) => call._id);

        await bulkDeleteCallRecords({
          variables: {
            input: {
              ids: explicitIds,
              deskId: desk._id,
            },
          },
        });
      } else if (selectAll) {
        // No filters applied - safe to use 'except' approach for all desk records
        await bulkDeleteCallRecords({
          variables: {
            input: {
              except: selection,
              deskId: desk._id,
            },
          },
        });
      } else {
        await bulkDeleteCallRecords({
          variables: {
            input: {
              ids: selection,
              deskId: desk._id,
            },
          },
        });
      }

      setBulkDeleteImpacted(0);

      // Pass bulk operation details to onUpdateApplied
      onUpdateApplied &&
        onUpdateApplied(null, null, {
          type: 'delete',
          affectedRecords: affectedRecords,
        });
    } catch (error) {
      console.error(error);
      onExit && onExit();
    }
  };

  /**
   * Handles bulk delete side effects
   **/
  const handleBulkDelete = () => {
    const totalImpacted = selectAll
      ? refetch({
          email: user?.email,
          azureId: user?.azure_id,
          where: {
            deskId: filterParams?.deskId,
            search: filterParams?.query,
            statuses: filterParams?.filter,
          },
          order: {
            statusThenOccurrence: true,
          },
          skip: 0,
          take: 1,
        })
          .then(({ data }) => data?.callRecords?.meta?.total ?? 0)
          .then((totalCount) => totalCount - selectExcept.size)
      : Promise.resolve(selectExcept.size);

    return totalImpacted.then((impactedCount) => {
      setBulkDeleteImpacted(impactedCount);
      setShowDeleteModal(true);
    });
  };

  return (
    <>
      <DeleteContainer
        data-cy={CALL_LIST.BULK_DELETE}
        onClick={handleBulkDelete}
        someSelected={someSelected}
      >
        <div style={{ marginRight: '10px' }}>
          <DeleteIcon
            inline
            width={14}
            height={14}
            color={someSelected ? colors.utaBlack : colors.disabledGrey}
          />
        </div>
        Delete
      </DeleteContainer>
      {showDeleteModal && (
        <DeleteModal
          isOpen
          impactedCount={bulkDeleteImpacted}
          onCancel={() => {
            setBulkDeleteImpacted(0);
            onExit && onExit();
            setShowDeleteModal(false);
          }}
          onConfirm={submitBulkDelete}
        />
      )}
    </>
  );
};

export default BulkDeleteCommunication;
